import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { directusOperations } from '../../lib/directus';
import { ProductIncentive, Promotion, ProductsState } from '../../lib/types';
import { getWIBTimeForDirectus } from '../../lib/timeUtils';

// Async thunks for API calls
export const fetchProductIncentives = createAsyncThunk(
  'products/fetchProductIncentives',
  async (limit = 20, { rejectWithValue }) => {
    try {
      return await directusOperations.getProductIncentives(limit);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch product incentives');
    }
  },
  {
    condition: (limit, { getState }) => {
      const state = getState() as { products: ProductsState };
      const now = Date.now();
      const lastFetched = state.products.incentivesLastFetched;
      const cacheTTL = state.products.incentivesCacheTTL;

      // Skip if cache is still fresh
      if (lastFetched) {
        const lastFetchedTime = new Date(lastFetched).getTime();
        if (!isNaN(lastFetchedTime) && (now - lastFetchedTime) < cacheTTL) {
          return false; // Skip the API call
        }
      }
      return true; // Proceed with the API call
    }
  }
);

export const fetchPromotions = createAsyncThunk(
  'products/fetchPromotions',
  async (limit = 20, { rejectWithValue }) => {
    try {
      return await directusOperations.getPromotions(limit);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch promotions');
    }
  },
  {
    condition: (limit, { getState }) => {
      const state = getState() as { products: ProductsState };
      const now = Date.now();
      const lastFetched = state.products.promotionsLastFetched;
      const cacheTTL = state.products.promotionsCacheTTL;

      // Skip if cache is still fresh
      if (lastFetched) {
        const lastFetchedTime = new Date(lastFetched).getTime();
        if (!isNaN(lastFetchedTime) && (now - lastFetchedTime) < cacheTTL) {
          return false; // Skip the API call
        }
      }
      return true; // Proceed with the API call
    }
  }
);

export const fetchIncentiveById = createAsyncThunk(
  'products/fetchIncentiveById',
  async (incentiveId: string, { rejectWithValue }) => {
    try {
      return await directusOperations.getIncentiveById(incentiveId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch incentive details');
    }
  }
);

export const fetchPromotionById = createAsyncThunk(
  'products/fetchPromotionById',
  async (promotionId: string, { rejectWithValue }) => {
    try {
      return await directusOperations.getPromotionById(promotionId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch promotion details');
    }
  }
);

// Coupon async thunks
export const fetchCoupons = createAsyncThunk(
  'products/fetchCoupons',
  async (limit = 20, { rejectWithValue }) => {
    try {
      return await directusOperations.getCoupons(limit);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch coupons');
    }
  }
);

export const fetchUserCoupons = createAsyncThunk(
  'products/fetchUserCoupons',
  async (userId: string | undefined = undefined, { rejectWithValue }) => {
    try {
      return await directusOperations.getUserCoupons(userId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch user coupons');
    }
  }
);

export const fetchCouponById = createAsyncThunk(
  'products/fetchCouponById',
  async (couponId: string, { rejectWithValue }) => {
    try {
      return await directusOperations.getCouponById(couponId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch coupon details');
    }
  }
);

export const issueCoupon = createAsyncThunk(
  'products/issueCoupon',
  async ({ couponId, userId }: { couponId: string; userId?: string }, { rejectWithValue }) => {
    try {
      return await directusOperations.issueCouponToUser(couponId, userId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to issue coupon');
    }
  }
);

export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (limit = 100, { rejectWithValue }) => {
    try {
      return await directusOperations.getProducts(limit);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch products');
    }
  },
  {
    condition: (limit, { getState }) => {
      const state = getState() as { products: ProductsState };
      const now = Date.now();
      const lastFetched = state.products.productsLastFetched;
      const cacheTTL = state.products.productsCacheTTL;

      // Skip if cache is still fresh
      if (lastFetched) {
        const lastFetchedTime = new Date(lastFetched).getTime();
        if (!isNaN(lastFetchedTime) && (now - lastFetchedTime) < cacheTTL) {
          return false; // Skip the API call
        }
      }
      return true; // Proceed with the API call
    }
  }
);

export const fetchProductById = createAsyncThunk(
  'products/fetchProductById',
  async (productId: string, { rejectWithValue }) => {
    try {
      return await directusOperations.getProductById(productId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch product details');
    }
  }
);

// Coupon Produk async thunks
export const fetchCouponProduk = createAsyncThunk(
  'products/fetchCouponProduk',
  async (_, { rejectWithValue }) => {
    try {
      return await directusOperations.getCouponProduk();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch coupon products');
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as { products: ProductsState };
      const now = Date.now();
      const lastFetched = state.products.couponProdukLastFetched;
      const cacheTTL = state.products.couponProdukCacheTTL || 30 * 60 * 1000; // 30 minutes default

      // Skip if cache is still fresh
      if (lastFetched) {
        const lastFetchedTime = new Date(lastFetched).getTime();
        if (!isNaN(lastFetchedTime) && (now - lastFetchedTime) < cacheTTL) {
          return false; // Skip the API call
        }
      }
      return true; // Proceed with the API call
    }
  }
);

export const fetchCouponProdukById = createAsyncThunk(
  'products/fetchCouponProdukById',
  async (couponId: number, { rejectWithValue }) => {
    try {
      return await directusOperations.getCouponProdukById(couponId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch coupon product details');
    }
  }
);

// Claim coupon product
export const claimCouponProduk = createAsyncThunk(
  'products/claimCouponProduk',
  async (couponUserId: number, { rejectWithValue }) => {
    try {
      return await directusOperations.claimCouponProduk(couponUserId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to claim coupon');
    }
  }
);

const initialState: ProductsState = {
  incentives: [],
  promotions: [],
  productPromotions: [], // Add product promotions state
  couponProduk: [], // Updated: Coupon products from couponUser with user filtering
  products: [],
  coupons: [], // Add coupon state
  userCoupons: [], // Add user coupons state
  incentivesLoading: false,
  promotionsLoading: false,
  productPromotionsLoading: false, // Add loading state for product promotions
  couponProdukLoading: false, // Updated: Loading state for user coupons
  couponProdukClaimLoading: false, // Add loading state for coupon claiming
  productsLoading: false,
  couponsLoading: false, // Add loading state for coupons
  userCouponsLoading: false, // Add loading state for user coupons
  error: null, // Keep for backwards compatibility
  incentivesError: null,
  promotionsError: null,
  productPromotionsError: null, // Add error state for product promotions
  couponProdukError: null, // Updated: Error state for user coupons
  couponsError: null, // Add error state for coupons
  userCouponsError: null, // Add error state for user coupons
  productsError: null,
  lastUpdated: null,
  incentivesLastFetched: null,
  promotionsLastFetched: null,
  productPromotionsLastFetched: null, // Add cache timestamp for product promotions
  couponProdukLastFetched: null, // Updated: Cache timestamp for user coupons
  productsLastFetched: null,
  userCouponsLastFetched: null, // Cache timestamp for user coupons
  incentivesCacheTTL: 30 * 60 * 1000, // 30 minutes in milliseconds
  promotionsCacheTTL: 15 * 60 * 1000, // 15 minutes in milliseconds
  productPromotionsCacheTTL: 30 * 60 * 1000, // 30 minutes for product promotions
  couponProdukCacheTTL: 30 * 60 * 1000, // Updated: Cache TTL for user coupons
  productsCacheTTL: 30 * 60 * 1000, // 30 minutes in milliseconds
  userCouponsCacheTTL: 30 * 60 * 1000, // Cache TTL for user coupons
};

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },

    clearIncentivesError: (state) => {
      state.incentivesError = null;
    },

    clearPromotionsError: (state) => {
      state.promotionsError = null;
    },

    clearProductsError: (state) => {
      state.productsError = null;
    },

    clearCouponsError: (state) => {
      state.couponsError = null;
    },

    clearAllErrors: (state) => {
      state.error = null;
      state.incentivesError = null;
      state.promotionsError = null;
      state.productsError = null;
      state.couponsError = null;
    },

    // Local data management for UI responsiveness
    updateIncentiveLocal: (state, action: PayloadAction<ProductIncentive>) => {
      const index = state.incentives.findIndex(incentive => incentive.id === action.payload.id);
      if (index >= 0) {
        state.incentives[index] = action.payload;
      }
    },

    updatePromotionLocal: (state, action: PayloadAction<Promotion>) => {
      const index = state.promotions.findIndex(promotion => promotion.id === action.payload.id);
      if (index >= 0) {
        state.promotions[index] = action.payload;
      }
    },

    updateProductLocal: (state, action: PayloadAction<any>) => {
      const index = state.products.findIndex(product => product.id === action.payload.id);
      if (index >= 0) {
        state.products[index] = action.payload;
      }
    },

    // Force refresh data
    invalidateCache: (state) => {
      state.incentivesLastFetched = null;
      state.promotionsLastFetched = null;
      state.productsLastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch product incentives
      .addCase(fetchProductIncentives.pending, (state) => {
        state.incentivesLoading = true;
        state.incentivesError = null;
      })
      .addCase(fetchProductIncentives.fulfilled, (state, action) => {
        state.incentivesLoading = false;
        state.incentives = action.payload;
        state.incentivesLastFetched = getWIBTimeForDirectus();
        state.lastUpdated = getWIBTimeForDirectus();
        state.incentivesError = null;
      })
      .addCase(fetchProductIncentives.rejected, (state, action) => {
        state.incentivesLoading = false;
        state.incentivesError = action.payload as string;
      })

      // Fetch promotions
      .addCase(fetchPromotions.pending, (state) => {
        state.promotionsLoading = true;
        state.promotionsError = null;
      })
      .addCase(fetchPromotions.fulfilled, (state, action) => {
        state.promotionsLoading = false;
        state.promotions = action.payload;
        state.promotionsLastFetched = getWIBTimeForDirectus();
        state.lastUpdated = getWIBTimeForDirectus();
        state.promotionsError = null;
      })
      .addCase(fetchPromotions.rejected, (state, action) => {
        state.promotionsLoading = false;
        state.promotionsError = action.payload as string;
      })

      // Fetch incentive by ID
      .addCase(fetchIncentiveById.pending, (state) => {
        state.incentivesError = null;
      })
      .addCase(fetchIncentiveById.fulfilled, (state, action) => {
        // Update incentive in list if it exists
        const index = state.incentives.findIndex(incentive => incentive.id === action.payload.id);
        if (index >= 0) {
          state.incentives[index] = action.payload;
        } else {
          // Add to list if not found
          state.incentives.push(action.payload);
        }
        state.incentivesError = null;
      })
      .addCase(fetchIncentiveById.rejected, (state, action) => {
        state.incentivesError = action.payload as string;
      })

      // Fetch promotion by ID
      .addCase(fetchPromotionById.pending, (state) => {
        state.promotionsError = null;
      })
      .addCase(fetchPromotionById.fulfilled, (state, action) => {
        // Update promotion in list if it exists
        const index = state.promotions.findIndex(promotion => promotion.id === action.payload.id);
        if (index >= 0) {
          state.promotions[index] = action.payload;
        } else {
          // Add to list if not found
          state.promotions.push(action.payload);
        }
        state.promotionsError = null;
      })
      .addCase(fetchPromotionById.rejected, (state, action) => {
        state.promotionsError = action.payload as string;
      })

      // Fetch products
      .addCase(fetchProducts.pending, (state) => {
        state.productsLoading = true;
        state.productsError = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.productsLoading = false;
        state.products = action.payload;
        state.productsLastFetched = getWIBTimeForDirectus();
        state.lastUpdated = getWIBTimeForDirectus();
        state.productsError = null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.productsLoading = false;
        state.productsError = action.payload as string;
      })

      // Fetch product by ID
      .addCase(fetchProductById.pending, (state) => {
        state.productsError = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        // Update product in list if it exists
        const index = state.products.findIndex(product => product.id === action.payload.id);
        if (index >= 0) {
          state.products[index] = action.payload;
        } else {
          // Add to list if not found
          state.products.push(action.payload);
        }
        state.productsError = null;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.productsError = action.payload as string;
      })

      // Fetch coupons
      .addCase(fetchCoupons.pending, (state) => {
        state.couponsLoading = true;
        state.couponsError = null;
      })
      .addCase(fetchCoupons.fulfilled, (state, action) => {
        state.couponsLoading = false;
        state.coupons = action.payload;
        state.couponsError = null;
      })
      .addCase(fetchCoupons.rejected, (state, action) => {
        state.couponsLoading = false;
        state.couponsError = action.payload as string;
      })

      // Fetch user coupons
      .addCase(fetchUserCoupons.pending, (state) => {
        state.couponsLoading = true;
        state.couponsError = null;
      })
      .addCase(fetchUserCoupons.fulfilled, (state, action) => {
        state.couponsLoading = false;
        state.userCoupons = action.payload;
        state.couponsError = null;
      })
      .addCase(fetchUserCoupons.rejected, (state, action) => {
        state.couponsLoading = false;
        state.couponsError = action.payload as string;
      })

      // Fetch coupon by ID
      .addCase(fetchCouponById.pending, (state) => {
        state.couponsError = null;
      })
      .addCase(fetchCouponById.fulfilled, (state, action) => {
        const index = state.coupons.findIndex(coupon => coupon.id === action.payload.id);
        if (index >= 0) {
          state.coupons[index] = action.payload;
        } else {
          state.coupons.push(action.payload);
        }
        state.couponsError = null;
      })
      .addCase(fetchCouponById.rejected, (state, action) => {
        state.couponsError = action.payload as string;
      })

      // Issue coupon
      .addCase(issueCoupon.pending, (state) => {
        state.couponsLoading = true;
        state.couponsError = null;
      })
      .addCase(issueCoupon.fulfilled, (state, action) => {
        state.couponsLoading = false;
        state.userCoupons.push(action.payload);
        state.couponsError = null;
      })
      .addCase(issueCoupon.rejected, (state, action) => {
        state.couponsLoading = false;
        state.couponsError = action.payload as string;
      })
      // Coupon Produk handlers
      .addCase(fetchCouponProduk.pending, (state) => {
        state.couponProdukLoading = true;
        state.couponProdukError = null;
      })
      .addCase(fetchCouponProduk.fulfilled, (state, action) => {
        state.couponProdukLoading = false;
        state.couponProduk = action.payload;
        state.couponProdukLastFetched = new Date().toISOString();
      })
      .addCase(fetchCouponProduk.rejected, (state, action) => {
        state.couponProdukLoading = false;
        state.couponProdukError = action.payload as string;
      })
      .addCase(fetchCouponProdukById.pending, (state) => {
        state.couponProdukLoading = true;
        state.couponProdukError = null;
      })
      .addCase(fetchCouponProdukById.fulfilled, (state, action) => {
        state.couponProdukLoading = false;
        // Update or add the coupon to the list
        const index = state.couponProduk.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.couponProduk[index] = action.payload;
        } else {
          state.couponProduk.push(action.payload);
        }
      })
      .addCase(fetchCouponProdukById.rejected, (state, action) => {
        state.couponProdukLoading = false;
        state.couponProdukError = action.payload as string;
      })
      .addCase(claimCouponProduk.pending, (state) => {
        state.couponProdukClaimLoading = true;
      })
      .addCase(claimCouponProduk.fulfilled, (state, action) => {
        state.couponProdukClaimLoading = false;

        // Update the claimed status of the coupon in the state
        const { couponUserId, claimedAt } = action.payload;

        // Find and update the coupon in couponProduk array
        const couponIndex = state.couponProduk.findIndex(coupon =>
          coupon.couponUserId === couponUserId
        );

        if (couponIndex !== -1) {
          state.couponProduk[couponIndex].claimed = true;
          state.couponProduk[couponIndex].claimedTime = claimedAt;
        }

        console.log('Coupon claimed successfully:', action.payload);
      })
      .addCase(claimCouponProduk.rejected, (state, action) => {
        state.couponProdukClaimLoading = false;
        state.couponProdukError = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearIncentivesError,
  clearPromotionsError,
  clearProductsError,
  clearCouponsError,
  clearAllErrors,
  updateIncentiveLocal,
  updatePromotionLocal,
  updateProductLocal,
  invalidateCache,
} = productsSlice.actions;

// Selectors
export const selectProducts = (state: { products: ProductsState }) => state.products;
export const selectIncentives = (state: { products: ProductsState }) => state.products.incentives;
export const selectPromotions = (state: { products: ProductsState }) => state.products.promotions;
export const selectCoupons = (state: { products: ProductsState }) => state.products.coupons;
export const selectUserCoupons = (state: { products: ProductsState }) => state.products.userCoupons;
export const selectIncentivesLoading = (state: { products: ProductsState }) => state.products.incentivesLoading;
export const selectPromotionsLoading = (state: { products: ProductsState }) => state.products.promotionsLoading;
export const selectCouponsLoading = (state: { products: ProductsState }) => state.products.couponsLoading;
export const selectProductsError = (state: { products: ProductsState }) => state.products.error;
export const selectCouponsError = (state: { products: ProductsState }) => state.products.couponsError;
export const selectIncentivesError = (state: { products: ProductsState }) => state.products.incentivesError;
export const selectPromotionsError = (state: { products: ProductsState }) => state.products.promotionsError;
export const selectProductsLastUpdated = (state: { products: ProductsState }) => state.products.lastUpdated;

// Cache status selectors
export const selectIncentivesNeedRefresh = (state: { products: ProductsState }) => {
  const { incentivesLastFetched, incentivesCacheTTL } = state.products;
  if (!incentivesLastFetched) return true;
  const now = Date.now();
  const lastFetched = new Date(incentivesLastFetched).getTime();
  // Handle invalid dates
  if (isNaN(lastFetched)) return true;
  return (now - lastFetched) >= incentivesCacheTTL;
};

export const selectPromotionsNeedRefresh = (state: { products: ProductsState }) => {
  const { promotionsLastFetched, promotionsCacheTTL } = state.products;
  if (!promotionsLastFetched) return true;
  const now = Date.now();
  const lastFetched = new Date(promotionsLastFetched).getTime();
  // Handle invalid dates
  if (isNaN(lastFetched)) return true;
  return (now - lastFetched) >= promotionsCacheTTL;
};

// Filtered selectors for active items only - memoized to prevent unnecessary re-renders
export const selectActiveIncentives = createSelector(
  [selectIncentives],
  (incentives) => {
    const now = new Date();
    return incentives.filter(incentive => {
      if (!incentive.is_active) return false;
      const validFrom = new Date(incentive.valid_from);
      const validTo = new Date(incentive.valid_to);
      return now >= validFrom && now <= validTo;
    });
  }
);

export const selectActivePromotions = createSelector(
  [selectPromotions],
  (promotions) => {
    const now = new Date();
    return promotions.filter(promotion => {
      if (!promotion.is_active) return false;
      const validFrom = new Date(promotion.valid_from);
      const validTo = new Date(promotion.valid_to);
      return now >= validFrom && now <= validTo;
    });
  }
);

// Helper selectors for specific incentive/promotion types
export const selectIncentiveById = (state: { products: ProductsState }, incentiveId: string) =>
  state.products.incentives.find(incentive => incentive.id === incentiveId);

export const selectPromotionById = (state: { products: ProductsState }, promotionId: string) =>
  state.products.promotions.find(promotion => promotion.id === promotionId);

// Ending soon promotions (within 7 days)
export const selectEndingSoonPromotions = (state: { products: ProductsState }) => {
  const now = new Date();
  const sevenDaysFromNow = new Date(now.getTime() + (7 * 24 * 60 * 60 * 1000));

  return state.products.promotions.filter(promotion => {
    if (!promotion.is_active) return false;
    const validTo = new Date(promotion.valid_to);
    return validTo > now && validTo <= sevenDaysFromNow;
  });
};

// Count selectors
export const selectActiveIncentivesCount = (state: { products: ProductsState }) =>
  selectActiveIncentives(state).length;

export const selectActivePromotionsCount = (state: { products: ProductsState }) =>
  selectActivePromotions(state).length;

// Product selectors
export const selectProductsList = (state: { products: ProductsState }) => state.products.products;
export const selectProductsLoading = (state: { products: ProductsState }) => state.products.productsLoading;

export const selectProductById = (state: { products: ProductsState }, productId: string) =>
  state.products.products.find(product => product.id === productId);

export const selectProductsNeedRefresh = (state: { products: ProductsState }) => {
  const { productsLastFetched, productsCacheTTL } = state.products;
  if (!productsLastFetched) return true;
  const now = Date.now();
  const lastFetched = new Date(productsLastFetched).getTime();
  if (isNaN(lastFetched)) return true;
  return (now - lastFetched) >= productsCacheTTL;
};

// Coupon Produk selectors
export const selectCouponProduk = (state: { products: ProductsState }) => state.products.couponProduk;
export const selectCouponProdukLoading = (state: { products: ProductsState }) => state.products.couponProdukLoading;
export const selectCouponProdukClaimLoading = (state: { products: ProductsState }) => state.products.couponProdukClaimLoading;
export const selectCouponProdukError = (state: { products: ProductsState }) => state.products.couponProdukError;
export const selectCouponProdukLastFetched = (state: { products: ProductsState }) => state.products.couponProdukLastFetched;

export const selectCouponProdukNeedRefresh = (state: { products: ProductsState }) => {
  const { couponProdukLastFetched, couponProdukCacheTTL } = state.products;
  if (!couponProdukLastFetched) return true;
  const now = Date.now();
  const lastFetched = new Date(couponProdukLastFetched).getTime();
  if (isNaN(lastFetched)) return true;
  return (now - lastFetched) >= couponProdukCacheTTL;
};

// Active coupon products (filter by status = 'active' and validity)
export const selectActiveCouponProduk = createSelector(
  [selectCouponProduk],
  (couponProduk) => {
    const now = new Date();
    return couponProduk.filter(coupon => {
      if (coupon.status !== 'active') return false;
      const validFrom = coupon.valid_from ? new Date(coupon.valid_from) : null;
      const validUntil = coupon.valid_until ? new Date(coupon.valid_until) : null;

      // Check date validity if dates are provided
      if (validFrom && validUntil) {
        return now >= validFrom && now <= validUntil;
      } else if (validFrom) {
        return now >= validFrom;
      } else if (validUntil) {
        return now <= validUntil;
      }
      return true; // No date constraints, just check status
    });
  }
);

export const selectCouponProdukById = (state: { products: ProductsState }, couponId: number) =>
  state.products.couponProduk.find(coupon => coupon.id === couponId);

export const selectActiveCouponProdukCount = (state: { products: ProductsState }) =>
  selectActiveCouponProduk(state).length;

export default productsSlice.reducer;